// orderHist page strings (views/OrderHistV2View.vue). Keys are read as $t('orderHist.<key>').
// Field codes (SA/TR/TO/KH, FPS/FPK, ycdb/ycdb2), pack aliases and server data stay as-is.
export default {
  en: {
    tour: {
      stepOf: 'Step {n} of {total}',
      skip: 'Skip',
      back: 'Back',
      next: 'Next',
      done: 'Got it',
      steps: {
        board: { title: 'Week board', body: 'Each column is one week. Click a week header or its empty space to pick that week, and hold the mouse and drag sideways to scroll. This week is marked (This Week); upcoming weeks with no package are half width.' },
        pack: { title: 'Packages', body: 'Click a package card to select it. The Info and Details tabs and the note box follow the selected package.' },
        packCam: { title: 'Copy a package as a picture', body: 'Hover a card to show its camera. Click it to put a picture of the card on the clipboard, then paste it (Ctrl+V) into Zalo, Messenger or email.' },
        weekCam: { title: 'Copy a whole week', body: 'Weeks with packages have a camera in the corner of their header: it copies the whole week column as one picture.' },
        pageCam: { title: 'Copy the whole page', body: 'Copies the customer page as it is on screen: profile, notes, week board and the open tab.' },
        composer: { title: 'Quick notes', body: 'Pick the note type and channel, type the note and press Ctrl+Enter to create it. The note is linked to the selected week and package.' },
        notesCam: { title: 'Search and copy notes', body: 'Search the notes here. The camera copies the notes shown, top to bottom (the first 20 at most); each note also has its own camera when you hover it.' },
        copyName: { title: 'Copy customer details', body: 'Copies the name, main address and phone, ready to paste into a message.' },
        addresses: { title: 'Addresses', body: 'Opens every address of the customer. The guide opens it for you now.' },
        tabs: { title: 'Tabs', body: 'Orders, Info, Details and Balance. In Details, each package table has its own camera too.' },
        jump: { title: 'Jump around', body: 'This week goes to the current week; Latest order goes to the customer’s newest package.' },
        boardGear: { title: 'Board options', body: 'The gear opens three options for this page. They are remembered in this browser.' },
        optLast3: { title: 'Last 3 months only', body: 'Loads only the last 3 months of orders so customers open faster. Turn it off to see the whole history.' },
        optAutoClean: { title: 'Auto-clean addresses', body: 'When creating a customer or adding an address, fills in the new ward and the district from the typed address.' },
        optNotesWeek: { title: 'Notes by week', body: 'Filters the notes to the selected week: pick a week on the board and only its notes show.' },
        settings: { title: 'Display settings', body: 'Click your name to open the display settings. The next steps go through them one by one.' },
        addrWindow: { title: 'The Addresses window', body: 'The header shows the customer name and phone. The camera copies the whole list as one picture, without the buttons.' },
        addrFilter: { title: 'Active or all', body: 'Active shows the addresses in use; All also shows deactivated ones.' },
        addrActions: { title: 'Make main or deactivate', body: 'Make main turns an address into the main one; Deactivate hides an address that is no longer used.' },
        addrNew: { title: 'Add an address', body: 'New address opens a short form for this customer.' },
        addrForm: { title: 'New address form', body: 'Type the address, pick the new ward (the district fills in by itself) and press Create address. This is only a preview: the guide does not save anything.' },
        newCust: { title: 'Add a customer', body: 'The green + next to the phone search opens the new customer form.' },
        custForm: { title: 'New customer form', body: 'Enter the phone first: it is checked against existing customers. Then the name, email (optional) and address, and pick the new ward; the district fills in by itself and the old ward is suggested from the address.' },
        custSave: { title: 'Save the customer', body: 'Create customer saves it, Cancel closes the form. Nothing is saved during this guide.' },
        setLang: { title: 'Language', body: 'Tiếng Việt or English for the whole app (the navbar stays the same).' },
        setOrderHist: { title: 'Classic or New', body: 'Switch the Order Hist page between the original design and this new one.' },
        setText: { title: 'Text size', body: 'Four sizes, applied to the new-style pages.' },
        setTheme: { title: 'Colour theme', body: 'Herb, Clay, Slate, or pick any colour with Custom.' },
        setBg: { title: 'Background', body: 'The colour of the page behind the cards: White, Stone, Mist or Custom.' },
        setSurface: { title: 'Surfaces', body: 'How white the cards, panels and inputs are: White, Soft or Dim, for a calmer screen.' },
        setGuide: { title: 'Replay this guide', body: 'Opens this guide again at any time.' },
        end: { title: 'Tips on hover', body: 'Hover any button to see what it does. That is all, enjoy the new Order Hist!' },
      },
    },
    capture: {
      pack: 'Copy this package as a picture',
      detail: 'Copy this package table as a picture',
      week: 'Copy this whole week as a picture',
      addresses: 'Copy these addresses as a picture',
      note: 'Copy this note as a picture',
      notes: 'Copy the notes shown as a picture (the first 20 at most)',
      body: 'Copy the whole page as a picture',
      failed: 'Could not copy the picture. Use Chrome or Edge and try again.',
    },
    search: {
      placeholder: 'Customer phone…',
      clear: 'Clear',
      newCustomer: 'New customer',
    },
    bar: {
      week: 'Week {week}',
      settings: 'View settings',
      thisWeek: 'This week',
      thisWeekTitle: 'Jump to the current week',
      latest: 'Latest order',
      latestTitle: "Jump to the customer's most recent package",
      last3: 'Last 3 months only',
      last3Hint: 'Faster load; untick for the full history.',
      last3Title: 'Only load the last 3 months of order history so a customer opens faster. Untick to load the whole history.',
      autoClean: 'Auto-clean address',
      autoCleanHint: 'Fill new ward and district from the typed address.',
      autoCleanTitle: 'Detect the district and new ward from the address when creating a customer or adding an address.',
      notesFollow: 'Notes follow the week',
      notesFollowHint: 'Filter notes by the selected week.',
      notesFollowTitle: 'Automatically filter the note list by week.',
    },
    legend: {
      holiday: 'holiday',
      meals: 'meals',
    },
    profile: {
      copy: 'Copy name / address / phone',
      notFound: 'Not found',
      noCustomer: 'No customer',
      notFoundHint: 'No customer has this phone.',
      searchHint: 'Search a phone above.',
      showDietTraits: 'Show Diet & Traits',
      hideDietTraits: 'Hide Diet & Traits to give Notes more room',
      address: 'Address',
      addresses: 'Addresses',
      otherAddressOne: '{count} other address',
      otherAddressMany: '{count} other addresses',
      diet: 'Diet',
      traits: 'Traits',
      dietTraitsSummary: 'Diet ({diet}) · Traits ({traits}) — show',
    },
    notes: {
      title: 'Notes',
      types: {
        order: 'Order',
        payment: 'Payment',
        FFShip: 'FPS',
        FFKhac: 'FPK',
        ship: 'Ship',
        cancel: 'Cancel',
        other: 'Other',
      },
      channels: {
        phone: 'Phone',
        facebook: 'Facebook',
        other: 'Other',
      },
      placeholder: 'Note description  (Ctrl+Enter to create)',
      pickWeek: 'Pick a week for the note',
      tooOld: 'Week {week} — notes only within 5 weeks',
      targetPack: 'Week {week} · {pack} #{id}',
      week: 'Week {week}',
      create: 'Create',
      search: 'Search notes',
      empty: 'No notes.',
      status: {
        done: 'done',
        pending: 'pending',
      },
    },
    board: {
      weeksGap: 'wk',
      week: 'week',
      holiday: 'holiday',
      thisWeek: '(This Week)',
      meals: '{count} meals',
      days: {
        mon: 'mon',
        tue: 'tue',
        wed: 'wed',
        thu: 'thu',
        fri: 'fri',
      },
    },
    tabs: {
      orderList: 'Orders',
      info: 'Info',
      details: 'Details',
      customerBalance: 'Balance',
    },
    orders: {
      title: "Customer's orders",
      clear: 'Clear',
      bill: 'Detailed bill',
      search: 'Search orders',
      cols: {
        week: 'Week',
        id: 'ID',
        pack: 'Pack',
        shipLoc: 'Ship loc',
        total: 'Total',
        mealNote: 'Meal note',
      },
      empty: 'No orders.',
    },
    info: {
      packInfo: 'Package info',
      pack: 'Pack',
      mealRequests: 'Meal requests',
      mealNote: 'Meal note',
      shipRequests: 'Delivery requests',
      shipLoc: 'Ship loc',
      address: 'Address',
      weeklyNote: 'Weekly note (2)',
      fixedNote: 'Fixed note (1)',
      receipt: 'Price breakdown',
      customer: 'Customer:',
      weekParen: '(week {week})',
      subtotal: 'Subtotal',
      discount: 'Discount',
      total: 'Total due',
      empty: 'Select a package on the board or in the order list.',
    },
    invoice: {
      pack: '{pack} ({meals} meals)',
      ship: 'Ship {loc} ({days} days)',
      shipOtherPack: ' (for ID: {id})',
      shipAdj: 'Shipping price adjustment',
      side: '{name} ({qty} x)',
      sideAdj: 'Side product price adjustment',
      special: 'ycdb service fee',
      other: 'Other',
    },
    details: {
      title: 'Package details for week',
      idParen: '(id: {id})',
      rows: {
        shipDis: 'ship loc',
        deliverTimeslot: 'time',
        shipper: 'shipper',
        address: 'address',
      },
      empty: 'No packages with meals in this week.',
    },
    balance: {
      title: 'Customer balance over the last 12 months',
      search: 'Search transactions',
      cols: {
        no: 'No.',
        date: 'Date',
        week: 'Week',
        food: 'Food',
        paid: 'Paid',
        due: 'Owed (credit)',
        description: 'Description',
      },
      empty: 'No transactions.',
    },
    addresses: {
      title: 'Addresses',
      active: 'Active',
      all: 'All',
      newAddress: 'New address',
      main: 'Main address',
      inactive: 'inactive',
      newWard: 'new ward',
      district: 'district',
      ward: 'ward',
      makeMain: 'Make main',
      deactivate: 'Deactivate',
      activate: 'Activate',
      empty: 'No other addresses.',
    },
    form: {
      newCustomer: 'New customer',
      newAddress: 'New address',
      phone: 'Customer phone',
      phonePlaceholder: '10 digits',
      email: 'Email',
      emailPlaceholder: 'email',
      name: 'Customer name',
      namePlaceholder: 'Customer name',
      address: 'Address',
      addressPlaceholder: 'Customer address',
      newWard: 'New ward',
      newWardPlaceholder: 'Type to search new ward…',
      district: 'District',
      districtPlaceholder: 'Type to search district…',
      ward: 'Ward',
      wardHint: '(from auto-clean)',
      cancel: 'Cancel',
      createCustomer: 'Create customer',
      createAddress: 'Create address',
      close: 'Close',
    },
    errors: {
      addressTooShort: 'The customer Address must have at least 10 characters',
      addressExists: 'This address already exists for this customer',
      pickCustomer: 'Please select a customer first',
      pickNewWard: 'Please select a New Ward',
      pickDistrict: 'Please select a District',
      nameMissing: 'The customer Name is missing',
      phoneMissing: 'The customer Phone is missing',
      addressMissing: 'The customer Address is missing',
      emailMissing: 'The customer Email is missing',
      phoneDigits: 'The customer Phone must have 10 digits',
      emailInvalid: 'The customer Email is not valid',
    },
  },
  vi: {
    tour: {
      stepOf: 'Bước {n}/{total}',
      skip: 'Bỏ qua',
      back: 'Quay lại',
      next: 'Tiếp',
      done: 'Đã hiểu',
      steps: {
        board: { title: 'Bảng tuần', body: 'Mỗi cột là một tuần. Bấm vào tiêu đề hoặc khoảng trống của cột để chọn tuần; giữ chuột và kéo ngang để cuộn. Tuần hiện tại có ghi (Tuần này); tuần sắp tới chưa có gói được thu gọn một nửa.' },
        pack: { title: 'Gói ăn', body: 'Bấm vào thẻ gói để chọn gói. Tab Thông tin, Chi tiết và ô ghi chú sẽ đi theo gói đang chọn.' },
        packCam: { title: 'Chụp ảnh một gói', body: 'Rê chuột lên thẻ để hiện nút máy ảnh. Bấm để chép ảnh thẻ vào clipboard, rồi dán (Ctrl+V) vào Zalo, Messenger hay email.' },
        weekCam: { title: 'Chụp cả tuần', body: 'Tuần có gói sẽ có nút máy ảnh ở góc tiêu đề: chép ảnh cả cột tuần đó.' },
        pageCam: { title: 'Chụp toàn trang', body: 'Chép ảnh trang khách đúng như trên màn hình: hồ sơ, ghi chú, bảng tuần và tab đang mở.' },
        composer: { title: 'Ghi chú nhanh', body: 'Chọn loại và kênh, gõ nội dung rồi bấm Ctrl+Enter để tạo. Ghi chú gắn với tuần và gói đang chọn.' },
        notesCam: { title: 'Tìm và chụp ghi chú', body: 'Tìm ghi chú ở đây. Nút máy ảnh chép ảnh các ghi chú đang hiện (tối đa 20 ghi chú đầu); mỗi ghi chú cũng có nút máy ảnh riêng khi rê chuột.' },
        copyName: { title: 'Sao chép thông tin khách', body: 'Chép tên, địa chỉ chính và SĐT để dán vào tin nhắn.' },
        addresses: { title: 'Địa chỉ', body: 'Mở tất cả địa chỉ của khách. Hướng dẫn sẽ mở giúp bạn ngay bây giờ.' },
        tabs: { title: 'Các tab', body: 'Đơn hàng, Thông tin, Chi tiết, Số dư. Ở tab Chi tiết, mỗi bảng gói cũng có nút máy ảnh riêng.' },
        jump: { title: 'Đi nhanh', body: 'Tuần này: về tuần hiện tại. Đơn mới nhất: về gói mới nhất của khách.' },
        boardGear: { title: 'Tùy chọn trang', body: 'Bánh răng mở ba tùy chọn cho trang này, được nhớ trên trình duyệt này.' },
        optLast3: { title: 'Chỉ 3 tháng gần nhất', body: 'Chỉ tải đơn hàng 3 tháng gần nhất để mở khách nhanh hơn. Bỏ chọn để xem toàn bộ lịch sử.' },
        optAutoClean: { title: 'Tự làm sạch địa chỉ', body: 'Khi tạo khách hoặc thêm địa chỉ, tự điền phường mới và quận từ địa chỉ đã nhập.' },
        optNotesWeek: { title: 'Ghi chú theo tuần', body: 'Lọc ghi chú theo tuần đang chọn: chọn một tuần trên bảng và chỉ ghi chú của tuần đó hiện ra.' },
        settings: { title: 'Cài đặt hiển thị', body: 'Bấm vào tên của bạn để mở cài đặt hiển thị. Các bước tiếp theo đi qua từng mục.' },
        addrWindow: { title: 'Cửa sổ Địa chỉ', body: 'Tiêu đề có tên và SĐT khách. Nút máy ảnh chép ảnh cả danh sách, không kèm các nút bấm.' },
        addrFilter: { title: 'Đang dùng hoặc Tất cả', body: 'Đang dùng: các địa chỉ còn dùng; Tất cả: hiện cả địa chỉ đã ngưng.' },
        addrActions: { title: 'Đặt làm chính hoặc ngưng dùng', body: 'Đặt làm chính: chuyển địa chỉ này thành địa chỉ chính; Ngưng dùng: ẩn địa chỉ không còn dùng.' },
        addrNew: { title: 'Thêm địa chỉ', body: 'Thêm địa chỉ mở một form ngắn cho khách này.' },
        addrForm: { title: 'Form địa chỉ mới', body: 'Gõ địa chỉ, chọn phường mới (quận tự điền) rồi bấm Tạo địa chỉ. Đây chỉ là xem trước: hướng dẫn không lưu gì cả.' },
        newCust: { title: 'Thêm khách mới', body: 'Nút + màu xanh cạnh ô tìm SĐT mở form khách mới.' },
        custForm: { title: 'Form khách mới', body: 'Nhập SĐT trước: hệ thống kiểm tra trùng với khách cũ. Sau đó tên, email (không bắt buộc), địa chỉ và chọn phường mới; quận tự điền và phường cũ được gợi ý từ địa chỉ.' },
        custSave: { title: 'Lưu khách', body: 'Bấm Tạo khách hàng để lưu, Huỷ để đóng form. Trong hướng dẫn này không có gì được lưu.' },
        setLang: { title: 'Ngôn ngữ', body: 'Tiếng Việt hoặc English cho toàn bộ ứng dụng (thanh menu giữ nguyên).' },
        setOrderHist: { title: 'Cũ hoặc Mới', body: 'Chuyển trang Order Hist giữa giao diện cũ và giao diện mới này.' },
        setText: { title: 'Cỡ chữ', body: 'Bốn cỡ chữ, áp dụng cho các trang giao diện mới.' },
        setTheme: { title: 'Màu giao diện', body: 'Herb, Clay, Slate, hoặc chọn màu bất kỳ ở Tuỳ chọn.' },
        setBg: { title: 'Màu nền', body: 'Màu của trang phía sau các thẻ: Trắng, Xám đá, Xám nhạt hoặc Tuỳ chọn.' },
        setSurface: { title: 'Độ trắng', body: 'Độ trắng của thẻ, khung và ô nhập: Trắng, Dịu hoặc Trầm, cho màn hình dịu mắt hơn.' },
        setGuide: { title: 'Xem lại hướng dẫn', body: 'Mở lại hướng dẫn này bất cứ lúc nào.' },
        end: { title: 'Gợi ý khi rê chuột', body: 'Rê chuột lên nút bất kỳ để xem nút đó làm gì. Chúc dùng Order Hist mới vui vẻ!' },
      },
    },
    capture: {
      pack: 'Chụp ảnh gói này',
      detail: 'Chụp ảnh bảng gói này',
      week: 'Chụp ảnh cả tuần này',
      addresses: 'Chụp ảnh danh sách địa chỉ',
      note: 'Chụp ảnh ghi chú này',
      notes: 'Chụp ảnh các ghi chú đang hiện (tối đa 20 ghi chú đầu)',
      body: 'Chụp ảnh toàn trang',
      failed: 'Không sao chép được ảnh. Hãy dùng Chrome hoặc Edge và thử lại.',
    },
    search: {
      placeholder: 'Số điện thoại khách…',
      clear: 'Xoá',
      newCustomer: 'Khách hàng mới',
    },
    bar: {
      week: 'Tuần {week}',
      settings: 'Tuỳ chọn hiển thị',
      thisWeek: 'Tuần này',
      thisWeekTitle: 'Chọn tuần hiện tại',
      latest: 'Đơn mới nhất',
      latestTitle: 'Đến gói ăn gần nhất của khách',
      last3: 'Chỉ 3 tháng gần nhất',
      last3Hint: 'Mở nhanh hơn; bỏ chọn để xem toàn bộ lịch sử.',
      last3Title: 'Chỉ tải lịch sử đơn hàng trong 3 tháng gần nhất để mở khách hàng nhanh hơn. Bỏ chọn để tải toàn bộ lịch sử.',
      autoClean: 'Tự làm sạch địa chỉ',
      autoCleanHint: 'Tự điền phường mới và quận từ địa chỉ đã nhập.',
      autoCleanTitle: 'Tự động nhận diện Quận và Phường mới từ địa chỉ khi tạo khách hàng hoặc thêm địa chỉ.',
      notesFollow: 'Ghi chú theo tuần',
      notesFollowHint: 'Lọc ghi chú theo tuần đang chọn.',
      notesFollowTitle: 'Tự động lọc danh sách Note theo tuần.',
    },
    legend: {
      holiday: 'ngày nghỉ',
      meals: 'phần ăn',
    },
    profile: {
      copy: 'Sao chép tên / địa chỉ / SĐT',
      notFound: 'Không tồn tại',
      noCustomer: 'Chưa chọn khách',
      notFoundHint: 'Không có khách hàng với SĐT này.',
      searchHint: 'Tìm SĐT ở ô phía trên.',
      showDietTraits: 'Hiện Diet & Traits',
      hideDietTraits: 'Ẩn Diet & Traits để mở rộng phần Notes',
      address: 'Địa chỉ',
      addresses: 'Địa chỉ',
      otherAddressOne: '{count} địa chỉ khác',
      otherAddressMany: '{count} địa chỉ khác',
      diet: 'Diet',
      traits: 'Traits',
      dietTraitsSummary: 'Diet ({diet}) · Traits ({traits}) — hiện',
    },
    notes: {
      title: 'Ghi chú',
      // note types stay in English in both languages
      types: {
        order: 'Order',
        payment: 'Payment',
        FFShip: 'FPS',
        FFKhac: 'FPK',
        ship: 'Ship',
        cancel: 'Cancel',
        other: 'Other',
      },
      channels: {
        phone: 'Điện thoại',
        facebook: 'Facebook',
        other: 'Khác',
      },
      placeholder: 'Nội dung ghi chú  (Ctrl+Enter để tạo)',
      pickWeek: 'Chọn tuần cho ghi chú',
      tooOld: 'Tuần {week} — chỉ ghi chú trong 5 tuần',
      targetPack: 'Tuần {week} · {pack} #{id}',
      week: 'Tuần {week}',
      create: 'Tạo',
      search: 'Tìm ghi chú',
      empty: 'Chưa có ghi chú.',
      status: {
        done: 'xong',
        pending: 'đang chờ',
      },
    },
    board: {
      weeksGap: 'tuần',
      week: 'tuần',
      holiday: 'ngày nghỉ',
      thisWeek: '(Tuần này)',
      meals: '{count} meals', // kept in English on the week board
      days: {
        mon: 'T2',
        tue: 'T3',
        wed: 'T4',
        thu: 'T5',
        fri: 'T6',
      },
    },
    tabs: { // kept in English in both languages
      orderList: 'Orders',
      info: 'Info',
      details: 'Details',
      customerBalance: 'Balance',
    },
    orders: {
      title: 'Danh sách order của khách',
      clear: 'Bỏ chọn',
      bill: 'Bill chi tiết',
      search: 'Tìm đơn hàng',
      cols: {
        week: 'Tuần',
        id: 'ID',
        pack: 'Gói',
        shipLoc: 'Khu ship',
        total: 'Tổng',
        mealNote: 'Ghi chú phần ăn',
      },
      empty: 'Chưa có đơn hàng.',
    },
    info: {
      packInfo: 'Thông tin gói ăn',
      pack: 'Gói',
      mealRequests: 'Yêu cầu món ăn',
      mealNote: 'Ghi chú phần ăn',
      shipRequests: 'Yêu cầu ship',
      shipLoc: 'Khu ship',
      address: 'Địa chỉ',
      weeklyNote: 'Note 2 hàng tuần',
      fixedNote: 'Note 1 cố định',
      receipt: 'Chi tiết thành tiền',
      customer: 'Khách hàng:',
      weekParen: '(tuần {week})',
      subtotal: 'Tổng giá',
      discount: 'Giảm giá',
      total: 'Thành tiền',
      empty: 'Chọn một gói ăn trên bảng tuần hoặc trong danh sách đơn.',
    },
    invoice: {
      pack: '{pack} ({meals} phần)',
      ship: 'Ship {loc} ({days} ngày)',
      shipOtherPack: ' (theo ID: {id})',
      shipAdj: 'Điều chỉnh giá ship',
      side: '{name} ({qty} x)',
      sideAdj: 'Điều chỉnh giá sản phẩm phụ',
      special: 'Phí dịch vụ ycdb',
      other: 'Khác',
    },
    details: {
      title: 'Thông tin chi tiết các package trong tuần',
      idParen: '(id: {id})',
      rows: {
        shipDis: 'khu ship',
        deliverTimeslot: 'giờ giao',
        shipper: 'shipper',
        address: 'địa chỉ',
      },
      empty: 'Tuần này không có gói ăn nào.',
    },
    balance: {
      title: 'Số dư khách hàng trong 1 năm qua',
      search: 'Tìm giao dịch',
      cols: {
        no: 'STT',
        date: 'Ngày',
        week: 'Tuần',
        food: 'Tiền ăn',
        paid: 'Tiền trả',
        due: 'Thiếu (dư)',
        description: 'Diễn giải',
      },
      empty: 'Chưa có giao dịch.',
    },
    addresses: {
      title: 'Địa chỉ',
      active: 'Đang dùng',
      all: 'Tất cả',
      newAddress: 'Thêm địa chỉ',
      main: 'Địa chỉ chính',
      inactive: 'ngưng dùng',
      newWard: 'phường mới',
      district: 'quận',
      ward: 'phường',
      makeMain: 'Đặt làm chính',
      deactivate: 'Ngưng dùng',
      activate: 'Dùng lại',
      empty: 'Không có địa chỉ khác.',
    },
    form: {
      newCustomer: 'Khách hàng mới',
      newAddress: 'Thêm địa chỉ',
      phone: 'SĐT khách hàng',
      phonePlaceholder: '10 chữ số',
      email: 'Email',
      emailPlaceholder: 'email',
      name: 'Tên khách hàng',
      namePlaceholder: 'Tên khách hàng',
      address: 'Địa chỉ',
      addressPlaceholder: 'Địa chỉ khách hàng',
      newWard: 'Phường mới',
      newWardPlaceholder: 'Gõ để tìm phường mới…',
      district: 'Quận',
      districtPlaceholder: 'Gõ để tìm quận…',
      ward: 'Phường',
      wardHint: '(tự nhận diện)',
      cancel: 'Huỷ',
      createCustomer: 'Tạo khách hàng',
      createAddress: 'Tạo địa chỉ',
      close: 'Đóng',
    },
    errors: {
      addressTooShort: 'Địa chỉ khách hàng phải có ít nhất 10 ký tự',
      addressExists: 'Địa chỉ này đã có cho khách hàng',
      pickCustomer: 'Vui lòng chọn khách hàng trước',
      pickNewWard: 'Vui lòng chọn Phường mới',
      pickDistrict: 'Vui lòng chọn Quận',
      nameMissing: 'Thiếu tên khách hàng',
      phoneMissing: 'Thiếu SĐT khách hàng',
      addressMissing: 'Thiếu địa chỉ khách hàng',
      emailMissing: 'Thiếu email khách hàng',
      phoneDigits: 'SĐT khách hàng phải có 10 chữ số',
      emailInvalid: 'Email khách hàng không hợp lệ',
    },
  },
}
